import { Inject, Injectable, signal } from '@angular/core';
import { HttpService } from '@recriar/titan';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../../environments';
import {
  AttendanceFaceToFaceItem,
  AttendanceFaceToFaceRoom,
  CidAttendance,
} from '../../models/face-to-face/attendance-face-to-face.model';
import { UserModel, UserService } from '@recriar/mihmo-auth';
import {
  AttendanceCid,
  AttendanceReschedule,
  ClinicalRecordCheckResponse,
  MedicalLeaveHistoryItem,
} from '../../models/attendance-common.model';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AttendanceCommonService {
  private _notification$ = new Subject<void>();
  private userLogged: UserModel;

  private _finishButtonReloadSubject$ = new Subject<void>();

  private readonly KEY_CURRENT_ATTENDANCE = 'mihmo.current-attendance-selected';

  _loadingHeaderCustomerSelectedOnAttendance = signal<boolean>(false);
  isLoadingCustomerSelectedAttendance =
    this._loadingHeaderCustomerSelectedOnAttendance.asReadonly();

  constructor(
    private router: Router,
    private userService: UserService,
    private http: HttpService,
    @Inject('attendance') public firebase: AngularFireDatabase,
  ) {
    this.userLogged = this.userService.getUserLogged();
  }

  setLoadingHeaderCustomerSelectedOnAttendance(value: boolean) {
    this._loadingHeaderCustomerSelectedOnAttendance.set(value);
  }

  sendToQueue(attenda: Partial<AttendanceFaceToFaceItem>) {
    return this.http
      .disableApiPrefix()
      .post(
        `${environment.attendanceAPI}/scheduled-attendance/${attenda.id}/queue`,
        attenda.id,
      );
  }

  setAttendanceToSpecificMedic(
    attendanceId: string,
    squadUserId: string,
  ) {
    return this.http
      .disableApiPrefix()
      .patch<AttendanceFaceToFaceItem>(
        `${environment.attendanceAPI}/scheduled-attendance/${attendanceId}/squaduser`,
        {
          squadUserId: squadUserId,
          lock: false,
        },
      );
  }

  enterOrLeftOnRoom(
    userLoggedId = '',
    attenda: Partial<AttendanceFaceToFaceItem>,
    lock: boolean,
  ) {
    return this.http
      .disableApiPrefix()
      .patch<AttendanceFaceToFaceItem>(
        `${environment.attendanceAPI}/scheduled-attendance/${attenda.id}/squaduser`,
        {
          squadUserId: userLoggedId,
          lock: lock,
        },
      );
  }

  attendanceRoomById(
    attendanceId: string,
  ): Observable<AttendanceFaceToFaceItem> {
    return this.http
      .disableApiPrefix()
      .get<AttendanceFaceToFaceItem>(
        `${environment.attendanceAPI}/scheduled-attendance/${attendanceId}`,
      );
  }

  checkIfAttendanceCanFinish(
    attendanceId: string,
  ): Observable<{ canFinish: boolean }> {
    return this.http.disableApiPrefix().get<{
      canFinish: boolean;
    }>(`${environment.attendanceAPI}/scheduled-attendance/${attendanceId}/can-finish`);
  }

  getMedicalLeaveHistory(
    medicalExpertId: string,
  ): Observable<MedicalLeaveHistoryItem[]> {
    return this.http
      .disableApiPrefix()
      .get<MedicalLeaveHistoryItem[]>(
        `${environment.apiNest}/medical-expert-report-request/${medicalExpertId}/historic`,
      );
  }

  finalize(attendanceId: string): Observable<AttendanceFaceToFaceRoom> {
    return this.http
      .disableApiPrefix()
      .post<AttendanceFaceToFaceRoom>(
        `${environment.attendanceAPI}/scheduled-attendance/${attendanceId}/finish`,
        { id: attendanceId },
      );
  }

  updateAttendanceRealTime(initializing = true): void {
    if (!this.userLogged.companiesIds.length)

    this.userLogged.companiesIds.forEach((companyId) => {
      let newItems = false;
      const refChannel = `/attendances/faceToFace/${companyId}`;

      //TODO: Paliativo até o backend adicionar timestamp no firebase /attendances
      this.firebase.database.ref(`${refChannel}`).on('child_added', () => {
        if (!newItems) {
          return;
        }

        this.setNewNotification();
      });

      this.firebase.database.ref(`${refChannel}`).once('value', () => {
        newItems = true;

        if (initializing && newItems) {
          return;
        }

        this.setNewNotification();
      });

      this.firebase.database.ref(`${refChannel}`).on('child_removed', () => {
        this.setNewNotification();
      });

      this.firebase.database
        .ref(`${refChannel}`)
        .on('child_changed', (snapshot) => {
          this.setNewNotification();
        });
    });
  }

  get notification$(): Observable<void> {
    return this._notification$.asObservable();
  }

  setNewNotification() {
    this._notification$.next();
  }

  get finishButtonReloadSubject$(): Observable<void> {
    return this._finishButtonReloadSubject$.asObservable();
  }

  notifyFinishButtonReload() {
    this._finishButtonReloadSubject$.next();
  }

  noShow(attendance: Partial<AttendanceFaceToFaceItem>) {
    return this.http
      .disableApiPrefix()
      .post(
        `${environment.attendanceAPI}/scheduled-attendance/${attendance.id}/noshow`,
        {
          id: attendance.id,
        },
      );
  }

  changeType(attendance: Partial<AttendanceFaceToFaceItem>) {
    return this.http
      .disableApiPrefix()
      .patch(
        `${environment.attendanceAPI}/scheduled-attendance/${attendance.id}/change-type`,
        {},
      );
  }

  cancel(
    attendance: Partial<AttendanceFaceToFaceItem>,
    reason: string,
    userIdLogged: string,
  ) {
    return this.http
      .disableApiPrefix()
      .post(
        `${environment.attendanceAPI}/scheduled-attendance/${attendance.id}/cancel`,
        {
          canceledBy: userIdLogged,
          cancelReason: reason,
        },
      );
  }

  reschedule(payload: AttendanceReschedule) {
    return this.http
      .disableApiPrefix()
      .post(
        `${environment.attendanceAPI}/scheduled-attendance/reschedule-attendance`,
        payload,
      );
  }

  changeServiceType(
    attendanceId: string,
    newServiceType: string,
  ): Observable<AttendanceFaceToFaceItem> {
    const payload = {
      id: attendanceId,
      type: newServiceType,
    };

    return this.http
      .disableApiPrefix()
      .put<AttendanceFaceToFaceItem>(
        `${environment.attendanceAPI}/scheduled-attendance/change-service-type`,
        payload,
      );
  }

  saveCid(payload: AttendanceCid) {
    return this.http
      .disableApiPrefix()
      .post<CidAttendance>(
        `${environment.apiNest}/cid/create-cid-attendance`,
        payload,
      );
  }

  checkClinicalRecord(
    attendanceId: string,
  ): Observable<ClinicalRecordCheckResponse> {
    return this.http
      .disableApiPrefix()
      .get<ClinicalRecordCheckResponse>(
        `${environment.attendanceAPI}/scheduled-attendance/check-clinical-record/${attendanceId}`,
      );
  }

  redirectToRoom(scheduledAttendanceId, redirectedFromPreviousListPage: boolean, isRemote = false) {
    this.setCurrentAttendance(scheduledAttendanceId);

    this.router.navigate(
      [`/attendance/all/${scheduledAttendanceId}`],
      {
        state: { data: { redirectedFromList: redirectedFromPreviousListPage || false } },
      },
    );
  }

  setCurrentAttendance(attendanceId: string) {
    sessionStorage.setItem(
      this.KEY_CURRENT_ATTENDANCE,
      JSON.stringify(attendanceId),
    );
  }

  getCurrentAttendance(): string {
    return JSON.parse(sessionStorage.getItem(this.KEY_CURRENT_ATTENDANCE));
  }
}
